import React from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Props as ImpressionProps } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import {
  Paper,
  OpenalexWork,
  Author,
  PaperUpdateUnlinked,
} from 'js/social/shared/types';
import { NoThumbnailIcon } from 'js/social/shared/Icons';
import { TruncateHeight } from 'js/social/shared/SocialEditor/TruncateHeight';
import { Attachment } from 'js/liswp/build_vu/Attachment';
import { SimplePaperPodcast } from 'js/podcast_ai/SimplePaperPodcast';
import { RecommendedPodcastBadge } from 'js/podcast_ai/RecommendedPodcastBadge';
import _PaperThumbnail from "PaperAbstract-PaperThumbnail.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=PaperThumbnail";
import _ContentAndButtonsContainer from "PaperAbstract-ContentAndButtonsContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=ContentAndButtonsContainer";
import _ContentContainer from "PaperAbstract-ContentContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=ContentContainer";
import _PaperTitle from "PaperAbstract-PaperTitle.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=PaperTitle";
import _Abstract from "PaperAbstract-Abstract.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=Abstract";
import _SeeMoreButton from "PaperAbstract-SeeMoreButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=SeeMoreButton";
import _Container from "PaperAbstract-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=Container";
import _AttachmentContainer from "PaperAbstract-AttachmentContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=AttachmentContainer";
import _PodcastPlayerWrapper from "PaperAbstract-PodcastPlayerWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=PodcastPlayerWrapper";
import _PaperAuthorsAndJournal from "PaperAbstract-PaperAuthorsAndJournal.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=PaperAuthorsAndJournal";
import _AuthorLink from "PaperAbstract-AuthorLink.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CoreX/PaperAbstract.tsx?styleId=AuthorLink";


export type PaperSize = 'xsmall' | 'small' | 'medium' | 'large';

const LINE_HEIGHT = 20;

const PaperThumbnail = /*#__PURE__*/styled("div", null, {
  displayName: "PaperThumbnail",
  styles: _PaperThumbnail
});

const ContentAndButtonsContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ContentAndButtonsContainer",
  styles: _ContentAndButtonsContainer
});

const ContentContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ContentContainer",
  styles: _ContentContainer
});

const PaperTitle = /*#__PURE__*/styled(BodyLink, null, {
  displayName: "PaperTitle",
  styles: _PaperTitle
});

const Abstract = /*#__PURE__*/styled(Body, null, {
  displayName: "Abstract",
  styles: _Abstract
});

const SeeMoreButton = /*#__PURE__*/styled(Abstract, null, {
  displayName: "SeeMoreButton",
  styles: _SeeMoreButton
});

const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const AttachmentContainer = /*#__PURE__*/styled("div", null, {
  displayName: "AttachmentContainer",
  styles: _AttachmentContainer
});

const PodcastPlayerWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "PodcastPlayerWrapper",
  styles: _PodcastPlayerWrapper
});

const PaperAuthorsAndJournal = /*#__PURE__*/styled("div", null, {
  displayName: "PaperAuthorsAndJournal",
  styles: _PaperAuthorsAndJournal
});

const AuthorLink = /*#__PURE__*/styled(BodyLink, null, {
  displayName: "AuthorLink",
  styles: _AuthorLink
});

const PaperAuthor = ({
  author,
  size,
}: {
  author: Author;
  size?: 'xs' | 'sm';
}) =>
  author.profileUrl ? (
    <AuthorLink
      href={author.profileUrl}
      target={'_blank'}
      size={size}
      onClick={(e) => e.stopPropagation()}
    >
      {author.displayName}
    </AuthorLink>
  ) : (
    <Body as="span" size={size}>
      {author.displayName}
    </Body>
  );

type Props = {
  work: Paper | OpenalexWork | PaperUpdateUnlinked;
  canUseReader: boolean;
  showReader: () => void;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
  paperInteractions: React.ReactNode;
  size?: 'sm' | 'md';
  showSummary?: boolean;
  showPreview?: boolean;
  isSnippet?: boolean;
  variant?: 'default' | 'dense' | 'no_url' | 'library' | 'library-recommended';
  onPodcastReady?: () => void;
};

const PaperContainer: React.FunctionComponent<Props> = ({
  work,
  canUseReader,
  showReader,
  impressionPropsGen,
  paperInteractions,
  size = 'md',
  showSummary = true,
  showPreview = false,
  isSnippet = false,
  variant = 'default',
  onPodcastReady,
}) => {
  const { title, authors, publicationYear, publication, attachment, summary } =
    work;
  const url =
    variant === 'no_url' || variant === 'library-recommended'
      ? undefined
      : work.url;

  const publicationInfo: string[] = [];
  if (publicationYear) publicationInfo.push(publicationYear.toString());
  if (publication) publicationInfo.push(publication);

  const thumbnailImage =
    attachment?.thumbnailUrl != null ? (
      <img src={attachment.thumbnailUrl} alt="" />
    ) : (
      <NoThumbnailIcon />
    );

  let abstractHeightMultiplier = size === 'sm' ? 2 : 3;
  if (variant === 'dense') {
    // For mobile, show 2 lines in the abstract
    abstractHeightMultiplier = window.innerWidth > 567 ? 1 : 2;
  }

  const handleCardClick = (event?: React.MouseEvent) => {
    if (variant === 'dense') {
      canUseReader ? showReader() : window.open(url, '_blank');
    }

    // Used in Podcast Library
    if (
      variant === 'no_url' ||
      variant === 'library' ||
      variant === 'library-recommended'
    ) {
      if (!onPodcastReady) return;
      // Ignore click on text/"Read more" or author names
      if (event?.target instanceof Element) {
        const clickedElement = event.target as Element;
        if (
          clickedElement.closest('a[href*="/profile/"]') ||
          clickedElement.closest('[class*="TruncateHeight"]')
        ) {
          return;
        }
      }
      // Play the podcast instead
      SimplePaperPodcast.play(work.podcast?.workId);
    }
  };

  const content = (
    <>
      {showPreview && attachment ? null : canUseReader ? (
        <PaperThumbnail onClick={showReader} size={size} variant={variant}>
          {thumbnailImage}
        </PaperThumbnail>
      ) : (
        <PaperThumbnail
          as="a"
          href={url}
          target={'_blank'}
          size={size}
          variant={variant}
        >
          {thumbnailImage}
        </PaperThumbnail>
      )}
      <ContentAndButtonsContainer onClick={handleCardClick}>
        <ContentContainer
          data-coachmark-step="abstract-read-more"
          variant={variant}
        >
          {variant === 'library-recommended' && <RecommendedPodcastBadge />}
          <ImpressionWatcher
            stopPropagation={false}
            {...impressionPropsGen('work-card')}
          >
            {canUseReader ? (
              <PaperTitle size={size} onClick={showReader} variant={variant}>
                {title}
              </PaperTitle>
            ) : (
              <PaperTitle
                size={size}
                href={url}
                target={'_blank'}
                variant={variant}
              >
                {title}
              </PaperTitle>
            )}
            <PaperAuthorsAndJournal isSnippet={isSnippet} variant={variant}>
              {authors?.length > 0 && (
                <Body size={'sm'}>
                  by{' '}
                  {authors
                    .map((a) => (
                      <PaperAuthor
                        key={`${a.userId}:${a.displayName}`}
                        author={a}
                        size={'sm'}
                      />
                    ))
                    .flatMap((el) => [el, ', '])
                    .slice(0, -1)}
                </Body>
              )}
              {publicationInfo.length > 0 && (
                <Body size={'sm'}> {publicationInfo.join(', ')} </Body>
              )}
            </PaperAuthorsAndJournal>
            {showSummary && summary && (
              <TruncateHeight
                height={LINE_HEIGHT * abstractHeightMultiplier}
                readMoreImpressionProps={impressionPropsGen('see-more')}
                styledMoreElement={<SeeMoreButton variant={variant} />}
                verb="Read"
              >
                <Abstract size={'sm'}> Abstract: {summary} </Abstract>
              </TruncateHeight>
            )}
            {showPreview && attachment && attachment.id && (
              <AttachmentContainer onClick={showReader}>
                <Attachment
                  key={attachment.id.toString()}
                  shown
                  pages={1}
                  attachmentId={attachment.id.toString()}
                />
              </AttachmentContainer>
            )}
          </ImpressionWatcher>
        </ContentContainer>

        {work.podcast && (
          <PodcastPlayerWrapper className="podcast-player-wrapper">
            <SimplePaperPodcast
              workId={work.podcast.workId}
              workTitle={work.podcast.workTitle}
              showTitle={false}
              impressionSource="paper_to_podcast_newsfeed"
              onPlayerReady={onPodcastReady}
              showFeedbackSection={false}
              initialEpisode={work.podcast}
            />
          </PodcastPlayerWrapper>
        )}

        {paperInteractions}
      </ContentAndButtonsContainer>
    </>
  );

  return (
    <Container size={size} variant={variant}>
      {content}
    </Container>
  );
};

export default PaperContainer;
